import { FC } from 'react';
import { Control, useController } from 'react-hook-form';
import { Range } from 'react-range';
import styled, { useTheme } from 'styled-components';

import ValidationMessage from '@/components/Forms/ValidationMessage';
import Text from '@/components/Text';

import { FormDataT } from './OriginalVideoCard';

const Box = styled.div`
  padding: 16px 24px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  display: flex;
  align-items: center;
`;

const Sound = styled(Box)`
  width: 49px;
  height: 30px;
  padding: 0;
  justify-content: center;
`;

const Track = styled.div<{ $value: number }>`
  border-radius: 100px;
  margin: 0 16px;
  width: 272px;
  height: 7px;
  background: ${({ theme }) => theme.colors.lightGray};

  &::before {
    content: '';
    position: absolute;
    height: 100%;
    border-radius: 100px;
    background: ${({ theme }) => theme.colors.purple};
    width: ${({ $value }) => Math.floor($value * 100)}%;
    left: 0;
  }
`;

const Handle = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 100%;
  background: #cccccc;
  outline: none;
`;

const Icon = styled.svg`
  color: ${({ theme }) => theme.colors.gray};
`;

const Note = styled(Text).attrs({
  as: 'div',
  $type: 'smallBold',
  $color: 'gray',
})`
  margin-top: 16px;
  max-width: 425px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
  > svg {
    flex-shrink: 0;
  }
  > p {
    flex: 1;
  }
`;

interface Props {
  control: Control<FormDataT>;
}

const VideoVolumeField: FC<Props> = ({ control }) => {
  const theme = useTheme();
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController<FormDataT, 'settings.videoVolume'>({
    control,
    name: 'settings.videoVolume',
  });

  return (
    <div>
      <Box>
        <Icon
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20.52 7.22705C21.1468 7.85385 21.644 8.59796 21.9832 9.41691C22.3225 10.2359 22.497 11.1136 22.497 12C22.497 12.8864 22.3225 13.7642 21.9832 14.5831C21.644 15.4021 21.1468 16.1462 20.52 16.773"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7.5 15.75H3C2.80109 15.75 2.61032 15.671 2.46967 15.5303C2.32902 15.3897 2.25 15.1989 2.25 15V9C2.25 8.80109 2.32902 8.61032 2.46967 8.46967C2.61032 8.32902 2.80109 8.25 3 8.25H7.5L14.25 3V21L7.5 15.75Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7.5 8.25V15.75"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M17.8687 9.87866C18.1472 10.1572 18.3682 10.488 18.519 10.8519C18.6697 11.2159 18.7473 11.606 18.7473 12C18.7473 12.3939 18.6697 12.7841 18.519 13.148C18.3682 13.512 18.1472 13.8427 17.8687 14.1213"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Icon>
        <Range
          step={1}
          min={0}
          max={100}
          values={[value * 100]}
          onChange={(values) => onChange(values[0] / 100)}
          renderTrack={({ props, children }) => (
            <Track {...props} $value={value}>
              {children}
            </Track>
          )}
          renderThumb={({ props }) => <Handle {...props} />}
        />
        <Sound>
          <Text $type="caption" as="span">
            {Math.floor(value * 100)}
          </Text>
        </Sound>
      </Box>
      {!!error && <ValidationMessage>{error.message}</ValidationMessage>}
      <Note>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="8" cy="8" r="7.5" stroke={theme.colors.lightGray} />
          <path
            d="M7.25 5.73529V4.5H8.75V5.73529H7.25ZM7.25 11.5V6.55882H8.75V11.5H7.25Z"
            fill={theme.colors.black}
          />
        </svg>

        <p>
          Please note, your computer's volume settings may vary. Make sure it is
          set to max
        </p>
      </Note>
    </div>
  );
};

export default VideoVolumeField;
